/**
 * Motion helpers for the design system (spec §6.10).
 *
 * Every animated overlay in `@/components/ui` (BottomSheet, Dialog, Toast,
 * SaveBar) uses these presets. Each preset has a reduced-motion variant that
 * replaces translation with a short opacity change, honouring
 * `prefers-reduced-motion` and the user's `reducedMotion` setting. Global CSS
 * additionally collapses CSS transitions/animations under the media query.
 */
import { useReducedMotion } from 'framer-motion'
import type { Variants } from 'framer-motion'

/** Durations in seconds, mirroring the --duration-* tokens. */
export const motionDuration = {
  micro: 0.12,
  control: 0.16,
  card: 0.22,
  sheet: 0.28,
  page: 0.32,
} as const

export const easeStandard: [number, number, number, number] = [0.2, 0.8, 0.2, 1]
export const easeExit: [number, number, number, number] = [0.4, 0, 1, 1]

/** True when the user asked for reduced motion (OS-level preference). */
export function usePrefersReducedMotion(): boolean {
  return useReducedMotion() === true
}

/* ------------------------------------------------------------------------ */
/* Variant presets                                                           */
/* ------------------------------------------------------------------------ */

export const overlayVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: motionDuration.control, ease: easeStandard } },
  exit: { opacity: 0, transition: { duration: motionDuration.control, ease: easeExit } },
}

/** Bottom sheet: slides up from the bottom edge. */
export const sheetVariants: Variants = {
  hidden: { y: '100%' },
  visible: { y: 0, transition: { duration: motionDuration.sheet, ease: easeStandard } },
  exit: { y: '100%', transition: { duration: motionDuration.sheet, ease: easeExit } },
}

export const sheetReducedVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: motionDuration.micro } },
  exit: { opacity: 0, transition: { duration: motionDuration.micro } },
}

/** Centered dialog: slight scale + fade. */
export const dialogVariants: Variants = {
  hidden: { opacity: 0, scale: 0.96, y: 8 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { duration: motionDuration.card, ease: easeStandard } },
  exit: { opacity: 0, scale: 0.97, y: 4, transition: { duration: motionDuration.control, ease: easeExit } },
}

export const dialogReducedVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: motionDuration.micro } },
  exit: { opacity: 0, transition: { duration: motionDuration.micro } },
}

/** Toast / save bar: small rise from the bottom. */
export const toastVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: motionDuration.card, ease: easeStandard } },
  exit: { opacity: 0, y: 8, transition: { duration: motionDuration.control, ease: easeExit } },
}

export const toastReducedVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: motionDuration.micro } },
  exit: { opacity: 0, transition: { duration: motionDuration.micro } },
}

/**
 * Returns the right variant set for the current reduced-motion preference.
 * Pass the normal and reduced preset; hooks must be called unconditionally.
 */
export function useMotionVariants(normal: Variants, reduced: Variants): Variants {
  const prefersReduced = usePrefersReducedMotion()
  return prefersReduced ? reduced : normal
}
